import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputLeftAddon,
  List,
  ListItem,
  Progress,
  Heading,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { useState } from 'react';
import { FaChevronRight } from 'react-icons/fa';

import { IconCalendar } from 'assets/icons';
import Datepicker from 'components/Datepicker';
import { ControllableDrawer } from 'components/Drawer';
import Loader from 'components/Loader';
import { ReactSelectAsyncPaginate } from 'components/ReactSelect';
import BalanceDetails from 'containers/TimeOff/RightDrawer/BalanceDetails';
import { MONTH_DAY_YEAR_DASH_FORMAT } from 'global/appConstants';
import { fetchPaginatedEmployee } from 'hooks/employee/useFetchPaginatedEmployee';
import { useFetchLeaveBalances } from 'hooks/timeOff/useFetchLeaveBalances';
import { useUserInfo } from 'store/useUserStore';
import { getAsyncSelectLoadOptions } from 'utils/helpers';

import SingleRequest from './SingleRequests';

const AccrualsBalances = () => {
  const userInfo = useUserInfo();

  const [searchParams, setSearchParams] = useState({
    date: new Date(),
    user: userInfo,
  });
  const [selectedBalance, setSelectedBalance] = useState(null);

  const {
    isOpen: isBalanceDetailsDrawerOpen,
    onClose: onBalanceDetailsDrawerClose,
    onOpen: onBalanceDetailsDrawerOpen,
  } = useDisclosure();

  const { data, status } = useFetchLeaveBalances({
    date: format(searchParams.date, MONTH_DAY_YEAR_DASH_FORMAT),
    user_id: searchParams.user.userId,
  });

  const employeeLoadOptions = (search: any, _: any, { page }: any) => {
    return getAsyncSelectLoadOptions(fetchPaginatedEmployee, { search, page, active: '1' });
  };

  const handleUserChange = (user: any) => {
    setSearchParams(prevData => ({ ...prevData, user: user }));
  };

  const handleDateChange = (date: any) => {
    if (date) {
      setSearchParams(prevData => ({ ...prevData, date: date }));
    }
  };

  const handleListItemClick = (data: any) => {
    onBalanceDetailsDrawerOpen();
    setSelectedBalance({ ...data, user: searchParams.user });
  };

  return (
    <>
      <ControllableDrawer isOpen={isBalanceDetailsDrawerOpen} onClose={onBalanceDetailsDrawerClose}>
        <BalanceDetails onClose={onBalanceDetailsDrawerClose} balanceDetails={selectedBalance} />
      </ControllableDrawer>
      <Flex justifyContent="space-between" mb="4" flexDirection={{ base: 'column', sm: 'row' }} gridGap="3">
        <InputGroup width={{ sm: '250px' }}>
          <InputLeftAddon children="User" fontSize="sm" />
          <ReactSelectAsyncPaginate
            size="md"
            fontSize="sm"
            isSearchable
            loadOptions={employeeLoadOptions}
            additional={{
              page: 1,
            }}
            placeholder={'Select User'}
            onChange={handleUserChange}
            value={searchParams.user}
            getOptionValue={(option: any) => option.userId}
            getOptionLabel={(option: any) => option.firstName + ' ' + option.lastName}
          />
        </InputGroup>

        <InputGroup width={{ sm: '200px' }}>
          <InputLeftAddon
            children={<Icon as={IconCalendar} height="20px" width="20px" sx={{ '& path': { fill: 'gray.500' } }} />}
            px="2"
          />
          <Datepicker
            customInput={
              <Input
                type="text"
                fontSize="sm"
                variant="filled"
                border="1px"
                borderColor="inherit"
                autoComplete="false"
                width="100%"
                size="md"
                borderLeftRadius="0"
                borderRightRadius="0"
                readOnly
              />
            }
            placeholderText="Select Date"
            monthsShown={1}
            isClearable={false}
            selected={searchParams.date}
            onChange={handleDateChange}
            popperPlacement="bottom-start"
          />
        </InputGroup>
      </Flex>
      {status === 'loading' && <Loader />}{' '}
      {status === 'success' && (
        <>
          <List>
            {Object.values(data?.payload?.balances || {})?.length
              ? Object.values(data?.payload?.balances).map((item: any) => (
                  <ListItem
                    px={{ md: '2' }}
                    py="4"
                    display="flex"
                    key={item.category_id}
                    alignItems={{ md: 'center' }}
                    justifyContent={{ md: 'space-between' }}
                    flexDirection={{ base: 'column', md: 'row' }}
                    borderRadius="lg"
                    _hover={{
                      background: 'primary.50',
                    }}
                    cursor="pointer"
                    onClick={() => handleListItemClick(item)}
                  >
                    <Flex alignItems="center" flex={{ md: '0 0 180px', lg: '0 0 280px' }} mb={{ base: '2', md: '0' }}>
                      <Box
                        borderRadius="100%"
                        width="14px"
                        height="14px"
                        background={item?.color || 'primary.900'}
                        mr="3"
                        mt="0.5"
                      />
                      <Text fontSize="md" fontWeight="semibold" isTruncated>
                        {item.category_name}
                      </Text>
                    </Flex>
                    <Flex alignItems="center" flex="1 1 auto" gridGap={{ base: '10px', md: '20px' }}>
                      <Text flex={{ lg: '0 0 110px' }}>{item.balance}</Text>{' '}
                      <Progress
                        flex="1 1 auto"
                        value={item.percentage}
                        height="5"
                        colorScheme="primary"
                        borderRadius="md"
                      />{' '}
                      <Text flex={{ lg: '0 0 100px' }}>{item.used}</Text>
                      <Icon as={FaChevronRight} color="primary.500" />
                    </Flex>
                  </ListItem>
                ))
              : null}
          </List>
          <Heading as="h6" size="sm" mt="10">
            Requests
          </Heading>
          <SingleRequest data={data?.payload?.approved_requests || []} />
        </>
      )}
    </>
  );
};

export default AccrualsBalances;
